import { Link } from "react-router-dom";
import { Lock, CheckCircle2, ArrowRight, Clock } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { KLARNA_ENABLED } from "../config";

export default function CourseCard({ course, index = 0 }) {
  const { t } = useLang();
  const { slug, code, title, subtitle, accent, description, price_cents, owned, locked, progress_percent, completed } = course;

  return (
    <Link
      to={`/course/${slug}`}
      data-testid={`course-card-${slug}`}
      className="card-glow group relative flex flex-col rounded-2xl overflow-hidden bg-[#121721] border border-white/10 hover:border-white/20 animate-float-up"
      style={{ animationDelay: `${index * 90}ms`, boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}
    >
      <div className="relative h-40 overflow-hidden">
        <img src={course.cover} alt={title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 20%, #121721 95%)` }} />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="font-mono text-[10px] tracking-[0.25em] px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}55` }}>
            {code}
          </span>
        </div>
        {owned && (
          <span className="absolute top-4 right-4 font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 flex items-center gap-1">
            <CheckCircle2 size={12} /> {t("card.active")}
          </span>
        )}
        {!owned && course.has_pdf === false && (
          <span className="absolute top-4 right-4 font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/40 flex items-center gap-1" data-testid={`course-comingsoon-${slug}`}>
            <Clock size={12} /> {t("card.comingSoon")}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-display text-2xl font-bold text-white leading-tight">{title}</h3>
        <p className="text-sm font-medium mt-1" style={{ color: accent }}>{subtitle}</p>
        <p className="text-sm text-slate-400 mt-3 leading-relaxed line-clamp-2 flex-1">{description}</p>

        <div className="flex items-center gap-3 mt-4 text-xs text-slate-500 font-mono">
          <span>{course.topics.length} {t("card.modules")}</span>
        </div>

        {owned && (
          <div className="mt-4">
            <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${progress_percent}%`, backgroundColor: accent }} />
            </div>
            <span className="text-[11px] text-slate-400 font-mono mt-1.5 block">{progress_percent}% {t("card.completedShort")}{completed ? ` · ${t("card.completed")}` : ""}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/5">
          {owned ? (
            <span className="text-sm font-semibold text-emerald-300">{t("card.goTo")}</span>
          ) : (
            <div className="flex flex-col">
              <span className="font-display text-xl font-extrabold text-white">
                €{(price_cents / 100).toFixed(0)}
              </span>
              {KLARNA_ENABLED && (
                <span className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5" data-testid={`course-installment-${slug}`}>
                  <img src="/klarna.svg" alt="Klarna" className="h-3.5 w-auto" />
                  {t("card.installment", { amount: (price_cents / 300).toFixed(2).replace(".", ",") })}
                </span>
              )}
            </div>
          )}
          <span className="h-9 w-9 rounded-full flex items-center justify-center transition-all group-hover:translate-x-1" style={{ backgroundColor: `${accent}22`, color: accent }}>
            {locked && !owned ? <Lock size={15} /> : <ArrowRight size={16} />}
          </span>
        </div>
      </div>
    </Link>
  );
}
